"use client"

/**
 * Guide plates — the "thumbnails".
 *
 * Every one is drawn, not photographed: a technical-illustration take on what
 * the guide actually does, built from the same `--pg-*` glass tokens as the
 * landing so a plate reads as part of the product rather than as stock art.
 *
 * Each plate carries the guide's accent through `--acc` (an "R G B" triple).
 * That single variable drives the atmosphere behind the drawing, the gradient
 * on whatever the drawing's "solid ink" element is, and the glow it throws —
 * which is how five plates cut from one template still read as five subjects.
 *
 * Layers, back to front: accent bloom → drafting grid → the drawing → gloss →
 * vignette → caption. The grid is now the QUIETEST layer, not the loudest.
 */

import Image from "next/image"
import {
  BookOpen,
  Dumbbell,
  GraduationCap,
  Plane,
  Plus,
  ShoppingBag,
  Store,
  Ticket,
  UtensilsCrossed,
} from "lucide-react"

import { BoothMark } from "@/components/ui/booth-mark"
import { PulseDot } from "@/components/ui/pulse-dot"


export type PlateKind = "rail" | "plan" | "sign" | "grid" | "dial" | "channels" | "dossier" | "city" | "globe" | "partners" | "hours"

const PLATE_ANIMATIONS = `
@keyframes hp-travel {
  0%   { transform: translateX(0); opacity: 0; }
  10%  { opacity: 1; }
  86%  { opacity: 1; }
  100% { transform: translateX(var(--hp-travel, 118px)); opacity: 0; }
}
@keyframes hp-ring {
  0%   { transform: scale(0.6); opacity: 0.5; }
  100% { transform: scale(2.2); opacity: 0; }
}
@keyframes hp-sweep {
  0%, 100% { opacity: 0.35; }
  50%      { opacity: 0.95; }
}
@keyframes hp-drift {
  0%, 100% { transform: translate3d(0, 0, 0) scale(1); }
  50%      { transform: translate3d(2%, -3%, 0) scale(1.08); }
}
@keyframes hp-route {
  0%   { offset-distance: 0%; opacity: 0; }
  6%   { opacity: 1; }
  92%  { opacity: 1; }
  100% { offset-distance: 100%; opacity: 0; }
}
@keyframes hp-arrive {
  0%, 55%  { opacity: 0.25; transform: scale(0.85); }
  70%, 100% { opacity: 1; transform: scale(1); }
}
.hp-plate .hp-token { animation: hp-travel 3.6s cubic-bezier(0.65, 0, 0.35, 1) infinite; }
.hp-plate .hp-rider { animation: hp-route 6.4s cubic-bezier(0.45, 0, 0.25, 1) infinite; offset-rotate: 0deg; }
.hp-plate .hp-arrive { animation: hp-arrive 6.4s ease-out infinite; transform-origin: center; }
.hp-plate .hp-ring  { animation: hp-ring 2.8s ease-out infinite; }
.hp-plate .hp-sweep { animation: hp-sweep 3.2s ease-in-out infinite; }
.hp-plate .hp-bloom { animation: hp-drift 14s ease-in-out infinite; }
.hp-plate .hp-ink   { stroke-dasharray: 240; stroke-dashoffset: 240; transition: stroke-dashoffset 1.4s cubic-bezier(0.22,1,0.36,1); }
.hp-plate.is-live .hp-ink,
.group:hover .hp-plate .hp-ink { stroke-dashoffset: 0; }
.hp-plate .hp-spin  { animation: hp-spin 22s linear infinite; }
/* The viewBox is centred on 0,0, which is also the globe's centre, so a plain
   scale on the group zooms about the sphere without needing a transform
   origin. Scale only — an earlier version added a translate and the sphere
   walked up out of the frame on every cycle. One compositable property, two
   keyframes, no layout touched. */
.hp-plate .hp-orbit { animation: hp-orbit 20s ease-in-out infinite; }
.hp-plate .hp-glow { transition: opacity 700ms ease; }
.group:hover .hp-plate .hp-glow { opacity: 1; }
@keyframes hp-orbit {
  0%, 100% { transform: scale(0.97); }
  50%      { transform: scale(1.16); }
}
@keyframes hp-spin {
  from { transform: translateX(0); }
  to   { transform: translateX(-120px); }
}
@media (prefers-reduced-motion: reduce) {
  .hp-plate .hp-token,
  .hp-plate .hp-rider,
  .hp-plate .hp-arrive,
  .hp-plate .hp-ring,
  .hp-plate .hp-sweep,
  .hp-plate .hp-spin,
  .hp-plate .hp-orbit,
  .hp-plate .hp-bloom { animation: none; }
  .hp-plate .hp-ink { transition: none; stroke-dashoffset: 0; }
}

/* Phone overrides, opted into per plate. The ratio rides a custom property
   because the desktop ratio is inline, and inline beats a class. */
@media (max-width: 767.98px) {
  .hp-plate.hp-ratio-sm { aspect-ratio: var(--hp-ratio-sm) !important; }
}
`

/** Ink fill for whatever each drawing picks out — the accent, as a gradient. */
const INK = "linear-gradient(145deg, rgb(var(--acc) / 0.95) 0%, rgb(var(--acc) / 0.62) 100%)"

/** Atmosphere: two accent blooms, slowly drifting, well under the drawing. */
function Bloom() {
  return (
    <>
      <span
        aria-hidden
        className="hp-bloom pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(70% 60% at 24% 18%, rgb(var(--acc) / 0.30) 0%, transparent 70%), radial-gradient(55% 55% at 82% 88%, rgb(var(--acc) / 0.16) 0%, transparent 72%)",
        }}
      />
      <span
        aria-hidden
        className="hp-glow pointer-events-none absolute inset-0 opacity-0"
        style={{
          background: "radial-gradient(60% 50% at 50% 50%, rgb(var(--acc) / 0.18) 0%, transparent 72%)",
        }}
      />
    </>
  )
}

/** Drafting grid, faded down so the gradient reads first. */
function Blueprint() {
  return (
    <span
      aria-hidden
      className="pointer-events-none absolute inset-0"
      style={{
        backgroundImage:
          "linear-gradient(to right, var(--pg-line) 1px, transparent 1px), linear-gradient(to bottom, var(--pg-line) 1px, transparent 1px)",
        backgroundSize: "38px 38px",
        maskImage: "radial-gradient(120% 100% at 50% 40%, #000 20%, transparent 78%)",
        WebkitMaskImage: "radial-gradient(120% 100% at 50% 40%, #000 20%, transparent 78%)",
        opacity: 0.55,
      }}
    />
  )
}

export function HelpPlate({
  kind,
  accent,
  className = "",
  live = false,
  ratio = "16 / 10",
  mobileRatio,
  brand,
}: {
  kind: PlateKind
  /**
   * Figure number and caption. Neither is drawn any more: the plates go
   * without corner text, by request. Kept on the props because the
   * article and guide data still carry them.
   */
  figure?: number
  caption?: string
  /** "R G B" triple; poured into rgb(<accent> / a) throughout the drawing. */
  accent: string
  className?: string
  /** Runs the draw-in animations without waiting for a hover (hero use). */
  live?: boolean
  /**
   * Aspect ratio as a CSS value ("16 / 10"). Inline rather than an
   * `aspect-[16/10]` class on purpose — the plate has no intrinsic size, so an
   * arbitrary class that misses the build collapses it to nothing.
   */
  ratio?: string
  /** Aspect ratio below md, e.g. "4 / 5" for a taller hero on phones. */
  mobileRatio?: string
  /** Partner mark stamped into the drawing, where the guide has one. */
  brand?: { src: string; alt: string }
}) {
  return (
    <div
      aria-hidden
      className={`hp-plate relative overflow-hidden rounded-[22px] ${live ? "is-live" : ""} ${mobileRatio ? "hp-ratio-sm" : ""} ${className}`}
      style={{
        ["--acc" as string]: accent,
        ...(mobileRatio ? { ["--hp-ratio-sm" as string]: mobileRatio } : {}),
        aspectRatio: ratio,
        background: "var(--pg-card)",
        border: "1px solid var(--pg-card-edge)",
        boxShadow: "inset 0 1px 0 var(--pg-gloss), 0 24px 60px -46px rgba(0,0,0,0.95)",
      }}
    >
      {/* React 19 dedupes a <style> carrying href + precedence, so five plates
          on the index page inject one stylesheet, not five. */}
      <style href="booth-help-plates" precedence="default">
        {PLATE_ANIMATIONS}
      </style>

      <Bloom />
      <Blueprint />

      <div className="absolute inset-0">
        {kind === "rail" && <RailDrawing brand={brand} />}
        {kind === "plan" && <PlanDrawing />}
        {kind === "sign" && <SignDrawing />}
        {kind === "grid" && <GridDrawing />}
        {kind === "dial" && <DialDrawing />}
        {kind === "channels" && <ChannelsDrawing />}
        {kind === "dossier" && <DossierDrawing />}
        {kind === "city" && <CityDrawing />}
        {kind === "globe" && <GlobeDrawing />}
        {kind === "partners" && <PartnersDrawing />}
        {kind === "hours" && <HoursDrawing />}
      </div>

      {/* Gloss over the drawing, then a vignette to seat it in the frame. */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{ background: "linear-gradient(158deg, var(--pg-gloss) 0%, transparent 42%)", opacity: 0.45 }}
      />
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{ background: "radial-gradient(120% 90% at 50% 45%, transparent 55%, rgba(0,0,0,0.28) 100%)" }}
      />

    </div>
  )
}

/* Fig. 01 — the rail. Money leaving a client, crossing the platform, landing
   in a bank account. The token is the payment; the rings are it arriving. */
function RailDrawing({ brand }: { brand?: { src: string; alt: string } }) {
  return (
    <div className="absolute inset-0 grid place-items-center px-8">
      <div className="relative flex w-full max-w-[240px] items-center justify-between">
        <div
          className="absolute left-[26px] right-[26px] top-1/2 h-px -translate-y-1/2"
          style={{
            backgroundImage: "repeating-linear-gradient(to right, var(--pg-faint) 0 4px, transparent 4px 9px)",
          }}
        />

        {/* The payment leaves the client... */}
        <Node glyph="$" />

        {/* ...crosses the processor... */}
        <div className="absolute left-[26px] top-1/2 z-0 -translate-y-1/2">
          <span
            className="hp-token block h-[18px] w-[26px] rounded-[5px]"
            style={{
              // Travel span = rail width minus the token, kept as a var so the
              // keyframe doesn't hardcode a layout number.
              ["--hp-travel" as string]: "162px",
              background: INK,
              boxShadow: "0 6px 20px -6px rgb(var(--acc) / 0.7)",
            }}
          />
        </div>

        {brand && (
          // The mark sits ON the rail, mid-span — the money is drawn passing
          // through it, which is exactly what happens. Above the token in the
          // stack so the token reads as going behind the processor.
          <span
            className="absolute left-1/2 top-1/2 z-10 flex h-8 -translate-x-1/2 -translate-y-1/2 items-center rounded-full px-3"
            style={{
              background: "var(--pg-bg)",
              border: "1px solid var(--pg-card-edge)",
              boxShadow: "0 10px 30px -14px rgba(0,0,0,0.95), inset 0 1px 0 var(--pg-gloss)",
            }}
          >
            <Image
              src={brand.src}
              alt={brand.alt}
              width={40}
              height={17}
              className="h-[15px] w-auto object-contain"
            />
          </span>
        )}

        {/* ...and lands in the account. */}
        <div className="relative z-10">
          <span className="hp-ring absolute inset-0 rounded-full" style={{ border: "1px solid rgb(var(--acc) / 0.8)" }} />
          <Node glyph="▣" solid />
        </div>
      </div>
    </div>
  )
}

function Node({ glyph, solid = false }: { glyph: string; solid?: boolean }) {
  return (
    <span
      className="relative grid h-[52px] w-[52px] shrink-0 place-items-center rounded-full text-[15px]"
      style={
        solid
          ? { background: INK, color: "#0B0B0D", boxShadow: "0 10px 30px -12px rgb(var(--acc) / 0.8)" }
          : { background: "var(--pg-card)", border: "1px solid var(--pg-card-edge)", color: "var(--pg-muted)" }
      }
    >
      {glyph}
    </span>
  )
}

/* Fig. 02 — the floor plan. A shop drawn from above, chairs as squares, the
   one you're renting out picked out in accent ink. */
function PlanDrawing() {
  return (
    <div className="absolute inset-0 grid place-items-center px-9 py-3">
      <div className="relative w-full max-w-[228px] rounded-[10px] p-4" style={{ border: "1px solid var(--pg-card-edge)" }}>
        {/* Dimension line down the left edge — the drafting tell. */}
        <span className="absolute -left-3.5 top-4 bottom-4 w-px" style={{ background: "var(--pg-line)" }} />
        <span className="absolute -left-[19px] top-4 h-px w-2.5" style={{ background: "var(--pg-faint)" }} />
        <span className="absolute -left-[19px] bottom-4 h-px w-2.5" style={{ background: "var(--pg-faint)" }} />

        <div className="grid grid-cols-3 gap-2.5">
          {[0, 1, 2, 3, 4, 5].map((c) => (
            <span
              key={c}
              className={`block h-9 rounded-[6px] ${c === 4 ? "hp-sweep" : ""}`}
              style={
                c === 4
                  ? { background: INK, boxShadow: "0 10px 26px -12px rgb(var(--acc) / 0.85)" }
                  : { background: "var(--pg-card)", border: "1px solid var(--pg-card-edge)" }
              }
            />
          ))}
        </div>
        <span
          className="absolute -top-2 left-1/2 h-4 w-4 -translate-x-1/2 rounded-full"
          style={{ background: "var(--pg-bg)", border: "1px solid rgb(var(--acc) / 0.55)" }}
        />
      </div>
    </div>
  )
}

/* The hours plate. A chair's day laid out against the clock. Booked time as
   card, the gaps as accent ink: a morning before the first cut, an evening
   window, and a day the chair sits empty. Spans run 8 to 22, in hours from the
   left edge. The founder took the 3D chair off this plate on 2026-10-26; the
   schedule carries it alone, centred. */
const HOURS_ROWS: Array<Array<{ from: number; to: number; open?: boolean }>> = [
  [{ from: 8, to: 12, open: true }, { from: 12, to: 22 }],
  [{ from: 8, to: 17 }, { from: 17, to: 22, open: true }],
  [{ from: 8, to: 22, open: true }],
]

/** Fractal noise as an inline SVG tile — the grain on the open hours. Grey
    noise centred on mid grey, filtered in sRGB: overlaid, mid grey is
    neutral, so the grain shades the green without shifting it. */
const HOURS_GRAIN = `url("data:image/svg+xml;utf8,${encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" width="120" height="120"><filter id="n" color-interpolation-filters="sRGB"><feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="2" stitchTiles="stitch"/><feColorMatrix type="saturate" values="0"/><feComponentTransfer><feFuncA type="linear" slope="0" intercept="0.55"/></feComponentTransfer></filter><rect width="100%" height="100%" filter="url(#n)"/></svg>',
)}")`

function HoursDrawing() {
  const span = 22 - 8
  return (
    <div className="absolute inset-0">
      {/* The BOOTH mark, sunk into the bloom: large, accent-coloured, blurred
          well past legibility so it reads as light behind the schedule rather
          than a logo stamped on the plate. It rides the same drift as the
          bloom so the two move as one atmosphere. */}
      <span
        aria-hidden
        className="hp-bloom pointer-events-none absolute inset-0 grid place-items-center"
        style={{ color: "rgb(var(--acc))", opacity: 0.22, filter: "blur(14px)" }}
      >
        <BoothMark size={320} className="h-auto w-[62%] max-w-[360px] translate-y-[6%]" />
      </span>

      <div className="absolute inset-0 flex items-center justify-center px-[9%] py-3">
        <div className="relative w-full min-w-0 max-w-[380px]">
          {/* Hour ticks along the top — the drafting tell. */}
          <div className="relative mb-2.5 h-2">
            {[8, 12, 17, 22].map((h) => (
              <span
                key={h}
                className="absolute top-0 h-2 w-px"
                style={{ left: `${((h - 8) / span) * 100}%`, background: "var(--pg-faint)" }}
              />
            ))}
            <span className="absolute inset-x-0 bottom-0 h-px" style={{ background: "var(--pg-line)" }} />
          </div>

          <div className="flex flex-col gap-2.5">
            {HOURS_ROWS.map((row, r) => (
              <div key={r} className="relative h-[30px]">
                {row.map((b, i) => (
                  <span
                    key={i}
                    className="absolute inset-y-0 rounded-[6px]"
                    style={{
                      left: `calc(${((b.from - 8) / span) * 100}% + ${i ? 3 : 0}px)`,
                      right: `${((22 - b.to) / span) * 100}%`,
                      ...(b.open
                        ? {
                            // Solid, not the faded INK gradient, and no sweep or
                            // glow: a deep green that reads as ink, not neon. A
                            // medium film grain over it, overlaid so it shades
                            // the green rather than greying it.
                            backgroundColor: "rgb(var(--acc))",
                            backgroundImage: HOURS_GRAIN,
                            backgroundSize: "120px 120px",
                            backgroundBlendMode: "overlay",
                            boxShadow: "inset 0 1px 0 rgb(255 255 255 / 0.08), 0 10px 24px -14px rgba(0,0,0,0.9)",
                          }
                        : { background: "var(--pg-card)", border: "1px solid var(--pg-card-edge)" }),
                    }}
                  />
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

/* Fig. 03 — the signature. Three fanned agreements; the ink draws itself in
   on hover, which is the whole feeling of the feature. */
function SignDrawing() {
  return (
    <div className="absolute inset-0 grid place-items-center px-10">
      <div className="relative h-[132px] w-[176px]">
        {[2, 1, 0].map((i) => (
          <div
            key={i}
            className="absolute inset-0 rounded-[10px]"
            style={{
              background:
                i === 0
                  ? "linear-gradient(160deg, var(--pg-bg) 0%, rgb(var(--acc) / 0.10) 100%)"
                  : "var(--pg-card)",
              border: "1px solid var(--pg-card-edge)",
              transform: `translate(${i * 9}px, ${i * -7}px) rotate(${i * 2.4}deg)`,
              boxShadow: "0 18px 40px -30px rgba(0,0,0,0.95)",
            }}
          />
        ))}
        <div className="absolute inset-0 p-4">
          {[0.9, 0.72, 0.5].map((w, i) => (
            <span
              key={i}
              className="mb-2 block h-[3px] rounded-full"
              style={{ width: `${w * 100}%`, background: "var(--pg-line)" }}
            />
          ))}
          <svg viewBox="0 0 150 44" className="mt-4 h-11 w-full overflow-visible">
            <path
              className="hp-ink"
              d="M6 32 C 18 8, 26 6, 30 22 C 34 38, 42 40, 50 22 C 58 4, 66 6, 70 24 C 74 40, 86 34, 96 20 C 104 9, 118 12, 132 26"
              fill="none"
              stroke="rgb(var(--acc))"
              strokeWidth="1.8"
              strokeLinecap="round"
            />
            <line x1="4" y1="40" x2="146" y2="40" stroke="var(--pg-line)" strokeWidth="1" />
          </svg>
        </div>
      </div>
    </div>
  )
}

/* Fig. 04 — the board. Twelve chairs in a city, one of them open, and it's
   the one lit up. */
function GridDrawing() {
  const open = 6
  return (
    <div className="absolute inset-0 grid place-items-center px-10 py-2.5">
      <div className="grid w-full max-w-[212px] grid-cols-4 gap-2.5">
        {Array.from({ length: 12 }).map((_, i) => (
          <span key={i} className="relative block">
            <span
              className="block h-[38px] rounded-[7px]"
              style={
                i === open
                  ? { background: INK, boxShadow: "0 12px 28px -12px rgb(var(--acc) / 0.9)" }
                  : {
                      background: "var(--pg-card)",
                      border: "1px solid var(--pg-card-edge)",
                      opacity: 0.5 + ((i * 7) % 4) * 0.1,
                    }
              }
            />
            {i === open && (
              <span className="absolute -right-1 -top-1" style={{ color: "rgb(var(--acc))" }}>
                <PulseDot className="h-[5px] w-[5px]" />
              </span>
            )}
          </span>
        ))}
      </div>
    </div>
  )
}

/* Fig. 08 — the city. A grid of blocks from above, three shops on three
   sides of town, and one barber's route across it — the token rides the
   street from the first chair to the next, and the destination lights as
   it lands. The move IS the drawing. */
const CITY_ROUTE = "M 34 108 L 34 74 L 92 74 L 92 40 L 150 40 L 150 92 L 206 92"
// Every accent the journal has used, in order of first appearance. The globe
// is the one plate that is deliberately polychrome: the piece it illustrates
// is about a trade spread across cities, and a single accent would make that
// one place. The rim and meridians still take --acc, so it sits in the
// system rather than beside it.
const GLOBE_PALETTE = [
  "255 97 84",   // Product Hunt coral
  "129 140 248", // indigo
  "249 115 22",  // orange
  "56 189 248",  // sky
  "52 199 89",   // booth green
  "149 118 255", // violet
  "229 255 0",   // lime
  "59 130 246",  // blue
] as const

/* Continents as a dot matrix. Each is a rough outline in longitude and
   latitude (a drawing, not a survey), filled by sampling a regular grid and
   keeping the points that fall inside. Each continent takes one colour from
   the palette, so the globe stays polychrome and the land reads as land. */
const CONTINENTS: { rgb: string; outline: [number, number][] }[] = [
  // North America
  { rgb: GLOBE_PALETTE[3], outline: [[-168, 65], [-140, 70], [-95, 72], [-75, 68], [-60, 55], [-55, 48], [-70, 42], [-80, 30], [-82, 25], [-97, 20], [-90, 15], [-83, 9], [-78, 8], [-90, 14], [-105, 20], [-115, 30], [-125, 40], [-125, 50], [-140, 60], [-165, 60]] },
  // Greenland
  { rgb: GLOBE_PALETTE[3], outline: [[-55, 82], [-25, 82], [-20, 70], [-45, 60], [-55, 70]] },
  // South America
  { rgb: GLOBE_PALETTE[4], outline: [[-80, 10], [-62, 11], [-50, 0], [-35, -7], [-40, -22], [-58, -38], [-68, -55], [-75, -50], [-72, -30], [-80, -5]] },
  // Europe
  { rgb: GLOBE_PALETTE[1], outline: [[-10, 36], [0, 43], [-5, 48], [5, 52], [10, 58], [5, 62], [15, 70], [30, 71], [40, 66], [40, 45], [28, 40], [20, 38], [12, 38], [3, 42]] },
  // Africa
  { rgb: GLOBE_PALETTE[2], outline: [[-17, 21], [-10, 35], [10, 37], [32, 31], [43, 12], [51, 12], [40, -5], [40, -15], [33, -27], [20, -35], [13, -20], [9, -1], [-8, 4], [-17, 14]] },
  // Arabia
  { rgb: GLOBE_PALETTE[2], outline: [[35, 30], [48, 30], [57, 22], [52, 15], [43, 13], [37, 20]] },
  // Asia
  { rgb: GLOBE_PALETTE[5], outline: [[40, 45], [40, 66], [70, 73], [100, 78], [140, 72], [180, 68], [170, 60], [160, 55], [140, 52], [135, 40], [122, 30], [120, 22], [108, 18], [105, 10], [100, 2], [95, 15], [90, 22], [80, 12], [77, 8], [72, 20], [60, 25], [50, 30], [36, 35], [44, 40]] },
  // Indonesia
  { rgb: GLOBE_PALETTE[5], outline: [[95, 5], [120, 5], [140, -3], [140, -9], [115, -9], [105, -6]] },
  // Australia
  { rgb: GLOBE_PALETTE[6], outline: [[113, -22], [130, -12], [142, -11], [150, -22], [153, -28], [146, -39], [135, -35], [115, -34]] },
]

function insideOutline(lon: number, lat: number, poly: [number, number][]) {
  let inside = false
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const [xi, yi] = poly[i]
    const [xj, yj] = poly[j]
    if (yi > lat !== yj > lat && lon < ((xj - xi) * (lat - yi)) / (yj - yi) + xi) inside = !inside
  }
  return inside
}

// Grid: 2.5 units apart both ways. 120 units span 360° of longitude (3° per
// unit); latitude uses 0.6 units per degree so the 47-unit radius holds
// about 78°N to 78°S. Computed once, at module load.
const LAND_DOTS: [number, number, string][] = (() => {
  const step = 2.5
  const out: [number, number, string][] = []
  for (let y = -46; y <= 46; y += step) {
    const lat = -y / 0.6
    for (let x = step / 2; x < 120; x += step) {
      const lon = x * 3 - 180
      const hit = CONTINENTS.find((c) => insideOutline(lon, lat, c.outline))
      if (hit) out.push([x, y, hit.rgb])
    }
  }
  return out
})()

function GlobeDrawing() {
  const R = 47
  // One tile of the dot field, 120 wide: the whole world, longitude across.
  // Drawn twice, side by side, and the pair translates exactly one tile
  // width — so the seam never lands inside the sphere and the loop has no
  // visible jump. This is what reads as rotation; nothing actually rotates.
  const tile = (dx: number, keyPrefix: string) => (
    <g transform={`translate(${dx} 0)`}>
      {LAND_DOTS.map(([x, y, rgb], i) => (
        <circle key={`${keyPrefix}-${i}`} cx={x} cy={y} r={0.62} fill={`rgb(${rgb} / 0.9)`} />
      ))}
    </g>
  )

  return (
    <div className="absolute inset-0 grid place-items-center p-4">
      {/* overflow hidden, not visible: at this size nothing should spill, and
          a stray pin ring escaping the frame is worse than one being clipped. */}
      {/* The frame is 1.5× the globe's own 120 units, so the sphere draws at
          two thirds of the plate it used to fill. Still centred on 0,0. */}
      <svg viewBox="-90 -90 180 180" className="h-full w-full" style={{ maxWidth: 400, overflow: "hidden" }} fill="none">
        <defs>
          <clipPath id="hp-globe-clip">
            <circle cx={0} cy={0} r={R} />
          </clipPath>
          {/* Light from the upper left, so the sphere reads as a sphere and
              not a disc. */}
          <radialGradient id="hp-globe-shade" cx="32%" cy="26%" r="78%">
            <stop offset="0%" stopColor="rgb(255 255 255 / 0.07)" />
            <stop offset="70%" stopColor="rgb(255 255 255 / 0.02)" />
            <stop offset="100%" stopColor="rgb(0 0 0 / 0.30)" />
          </radialGradient>
        </defs>

        <g className="hp-orbit">
        <circle cx={0} cy={0} r={R} fill="url(#hp-globe-shade)" />

        <g clipPath="url(#hp-globe-clip)">
          {/* The travelling layer: cities, in every colour the journal uses. */}
          <g className="hp-spin">
            {tile(-60, "a")}
            {tile(60, "b")}
          </g>

          {/* Meridians ride with the dots so the whole surface turns together. */}
          <g className="hp-spin" stroke="var(--pg-faint)" strokeWidth={0.7}>
            {[-60, -20, 20, 60, 100].map((dx) => (
              <ellipse key={dx} cx={dx} cy={0} rx={18} ry={R} />
            ))}
          </g>

          {/* Parallels stay put — they are the axis the rest turns around. */}
          <g stroke="var(--pg-faint)" strokeWidth={0.7}>
            <ellipse cx={0} cy={0} rx={R} ry={16} />
            <ellipse cx={0} cy={0} rx={R * 0.86} ry={34} />
            <line x1={-R} y1={0} x2={R} y2={0} />
          </g>
        </g>

        {/* Rim last, over the clip, so the sphere has a clean edge. */}
        <circle cx={0} cy={0} r={R} stroke="var(--pg-muted)" strokeWidth={1.1} />

        {/* Three chairs open, somewhere. Pulsing on the same ring animation
            the other plates use, staggered so they do not beat as one. */}
        {([[-24, -18, 0], [21, -8, 0.9], [6, 24, 1.8]] as const).map(([cx, cy, delay], i) => (
          <g key={i}>
            <circle
              cx={cx}
              cy={cy}
              r={5}
              className="hp-ring"
              fill="none"
              stroke={`rgb(${GLOBE_PALETTE[i * 3]} / 0.9)`}
              strokeWidth={1.1}
              style={{ animationDelay: `${delay}s`, transformOrigin: `${cx}px ${cy}px` }}
            />
            <circle cx={cx} cy={cy} r={2.4} fill={`rgb(${GLOBE_PALETTE[i * 3]} / 0.95)`} />
          </g>
        ))}
        </g>
      </svg>
    </div>
  )
}

/* Fig. 09 — network roots. Booth rent is the root on the left: the chair a
   barber pays for anyway, marked with the BOOTH rings. Roots grow right from it
   to reward partners — free-standing dots in the rewards teaser colours
   (Tailwind 500s: rose, sky, violet, emerald, amber, cyan, orange, indigo),
   each a thin tinted ring with its category icon in the same colour. The last dot is empty and dashed: the brand that has not
   joined yet, which is who the piece is written for. Points ride each root
   in the colour of the partner it reaches, and that partner rings as they
   land.

   Like the globe, this plate is polychrome — the partners carry the colour,
   so the article's accent is a neutral silver. */
/** Paths of public/images/booth-icon-black.svg, verbatim. */
const BOOTH_MARK_PATHS = [
  "M303.524 264.259C282.234 264.259 262.653 260.944 244.781 254.314C226.909 247.447 211.534 238.093 198.655 226.254C186.039 214.415 176.051 200.443 168.692 184.342C161.596 168.24 158.047 150.836 158.047 132.129C158.047 113.423 161.596 96.0186 168.692 79.9168C176.051 63.8151 186.039 49.8445 198.655 38.0053C211.534 26.166 226.909 16.9311 244.781 10.3008C262.653 3.43387 282.234 0 303.524 0C324.813 0 344.394 3.43387 362.266 10.3008C380.139 16.9311 395.383 26.166 407.999 38.0053C420.877 49.8445 430.864 63.8151 437.961 79.9168C445.32 96.0186 449 113.423 449 132.129C449 150.836 445.32 168.24 437.961 184.342C430.864 200.443 420.877 214.415 407.999 226.254C395.383 238.093 380.139 247.447 362.266 254.314C344.394 260.944 324.813 264.259 303.524 264.259ZM303.524 232.292C319.557 232.292 334.012 229.687 346.89 224.478C359.769 219.032 370.808 211.809 380.007 202.812C389.206 193.814 396.302 183.276 401.296 171.2C406.29 158.887 408.787 145.863 408.787 132.129C408.787 118.395 406.29 105.491 401.296 93.4141C396.302 81.1008 389.206 70.4454 380.007 61.4472C370.808 52.449 359.769 45.3455 346.89 40.1365C334.012 34.6901 319.557 31.9669 303.524 31.9669C287.491 31.9669 273.035 34.6901 260.157 40.1365C247.278 45.3455 236.239 52.449 227.04 61.4472C217.841 70.4454 210.745 81.1008 205.751 93.4141C200.757 105.491 198.261 118.395 198.261 132.129C198.261 145.863 200.757 158.887 205.751 171.2C210.745 183.276 217.841 193.814 227.04 202.812C236.239 211.809 247.278 219.032 260.157 224.478C273.035 229.687 287.491 232.292 303.524 232.292Z",
  "M186.784 131.774C186.784 151.48 184.656 169.524 180.398 185.908C176.141 202.053 170.022 215.942 162.039 227.576C154.057 239.21 144.346 248.232 132.904 254.643C121.463 261.053 108.425 264.259 93.7917 264.259C79.157 264.259 65.9861 261.053 54.2792 254.643C42.838 248.232 32.9933 239.21 24.7449 227.576C16.7627 215.942 10.643 202.053 6.38583 185.908C2.12864 169.524 0 151.48 0 131.774C0 112.304 1.99561 94.4968 5.98673 78.3517C10.2439 61.9692 16.3636 48.0792 24.3458 36.6826C32.3281 25.0486 42.0398 16.0269 53.481 9.61601C65.1884 3.20507 78.6252 0 93.7917 0C108.425 0 121.463 3.20507 132.904 9.61601C144.346 16.0269 154.057 25.0486 162.039 36.6826C170.022 48.0792 176.141 61.9692 180.398 78.3517C184.656 94.4968 186.784 112.304 186.784 131.774ZM157.649 131.774C157.649 116.578 156.319 102.332 153.658 89.0358C150.997 75.7401 146.873 64.2244 141.286 54.4902C135.964 44.7551 129.313 37.0389 121.33 31.3405C113.348 25.6422 104.169 22.7934 93.7917 22.7934C82.8822 22.7934 73.3039 25.6422 65.055 31.3405C57.073 37.0389 50.4211 44.7551 45.0997 54.4902C39.7782 64.2244 35.7871 75.7401 33.1263 89.0358C30.4655 102.332 29.1352 116.578 29.1352 131.774C29.1352 146.969 30.4655 161.215 33.1263 174.511C35.7871 187.807 39.7782 199.441 45.0997 209.413C50.4211 219.385 57.073 227.22 65.055 232.919C73.3039 238.616 82.8822 241.466 93.7917 241.466C104.434 241.466 113.747 238.616 121.729 232.919C129.711 227.22 136.364 219.385 141.685 209.413C147.006 199.441 150.997 187.807 153.658 174.511C156.319 161.215 157.649 146.969 157.649 131.774Z",
]
const ROOT = { x: 21, y: 70 }
const ROOT_GAP = 7
// The mark: 30.4 wide at its 449×265 aspect.
const MARK_W = 30.4
const MARK_H = (MARK_W * 265) / 449
const ROOT_OUT = ROOT.x + MARK_W / 2 + ROOT_GAP
const FIELD = { x: 122, y: 8, colW: 38, rowH: 42 }
const PARTNER_DOTS: { label: string; icon: typeof Plane; rgb: string | null }[] = [
  { label: "Products", icon: ShoppingBag, rgb: "244 63 94" },
  { label: "Travel", icon: Plane, rgb: "14 165 233" },
  { label: "Education", icon: GraduationCap, rgb: "139 92 246" },
  { label: "Wellness", icon: Dumbbell, rgb: "16 185 129" },
  { label: "Dining", icon: UtensilsCrossed, rgb: "229 255 0" },
  { label: "Hair shows", icon: Ticket, rgb: "6 182 212" },
  { label: "Classes", icon: BookOpen, rgb: "249 115 22" },
  { label: "Local", icon: Store, rgb: "99 102 241" },
  { label: "Your brand", icon: Plus, rgb: null },
]
const DOT_R = 8
// The roots land on the first dot of each row.
const ROOTED_ROWS = [0, 1, 2]
const ROOT_CYCLE = 6.4

function dotCenter(index: number) {
  return {
    x: FIELD.x + FIELD.colW * (index % 3) + FIELD.colW / 2,
    y: FIELD.y + FIELD.rowH * Math.floor(index / 3) + 14,
  }
}

function rootPath(row: number) {
  const { x, y } = dotCenter(row * 3)
  const end = x - DOT_R - 3
  return `M ${ROOT_OUT} ${ROOT.y} C ${ROOT_OUT + 40} ${ROOT.y}, ${ROOT_OUT + 34} ${y}, ${end} ${y}`
}

function PartnersDrawing() {
  return (
    // Block, not grid: as a grid item the svg's h-full resolves to auto and
    // the drawing sizes from its width, which spills out of a phone's 21:9
    // hero. A block child of an absolute box gets a real height to fit.
    <div className="absolute inset-0 px-5 py-6">
      <svg viewBox="0 0 240 144" className="mx-auto block h-full w-full" style={{ maxWidth: 560, overflow: "visible" }} fill="none">
        <style>{`
          @keyframes hp-land {
            0%, 86% { opacity: 0; transform: scale(0.8); }
            93%     { opacity: 1; transform: scale(1); }
            100%    { opacity: 0; transform: scale(1.35); }
          }
          .hp-plate .hp-land { animation: hp-land ${ROOT_CYCLE}s ease-out infinite; transform-box: fill-box; transform-origin: center; }
          @media (prefers-reduced-motion: reduce) { .hp-plate .hp-land { animation: none; } }
        `}</style>

        {/* Roots: a dashed line, the landing partner's colour inked over it. */}
        {ROOTED_ROWS.map((row) => {
          const rgb = PARTNER_DOTS[row * 3].rgb!
          return (
            <g key={row}>
              <path d={rootPath(row)} stroke="var(--pg-faint)" strokeWidth={0.7} strokeDasharray="2.4 3.2" />
              <path
                d={rootPath(row)}
                className="hp-ink"
                stroke={`rgb(${rgb} / 0.75)`}
                strokeWidth={1}
                strokeLinecap="round"
                style={{ strokeDasharray: 200, strokeDashoffset: 200 }}
              />
            </g>
          )
        })}
        {/* Hair roots — short, faint, going nowhere yet. */}
        {["M 64 70 C 74 64, 80 56, 92 54", "M 66 72 C 76 80, 82 88, 94 90"].map((d) => (
          <path key={d} d={d} stroke="var(--pg-line)" strokeWidth={0.8} strokeLinecap="round" />
        ))}
        {[[92, 54], [94, 90]].map(([cx, cy]) => (
          <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r={1.5} fill="var(--pg-faint)" />
        ))}

        {/* Partners: isolated dots, thin icons, no frame around them. */}
        {PARTNER_DOTS.map(({ label, icon: Icon, rgb }, i) => {
          const { x, y } = dotCenter(i)
          const row = Math.floor(i / 3)
          return (
            <g key={label}>
              {rgb && i % 3 === 0 && (
                <circle
                  cx={x}
                  cy={y}
                  r={DOT_R + 3}
                  className="hp-land"
                  stroke={`rgb(${rgb} / 0.8)`}
                  strokeWidth={0.6}
                  fill="none"
                  style={{ animationDelay: `${(row * ROOT_CYCLE) / 3}s` }}
                />
              )}
              <circle
                cx={x}
                cy={y}
                r={DOT_R}
                fill={rgb ? `rgb(${rgb} / 0.12)` : "transparent"}
                stroke={rgb ? `rgb(${rgb} / 0.55)` : "var(--pg-faint)"}
                strokeWidth={0.6}
                strokeDasharray={rgb ? undefined : "1.6 1.6"}
              />
              <Icon
                x={x - 3.6}
                y={y - 3.6}
                width={7.2}
                height={7.2}
                strokeWidth={1.5}
                style={{ color: rgb ? `color-mix(in srgb, rgb(${rgb}) 78%, var(--pg-fg))` : "var(--pg-muted)" }}
              />
              {/* Labels in the plate caption's voice: small, muted, tight —
                  the dots carry the colour, the words just name them. */}
              <text
                x={x}
                y={y + DOT_R + 7}
                textAnchor="middle"
                fontSize={4.5}
                fontWeight={500}
                letterSpacing={-0.06}
                fill="var(--pg-muted)"
              >
                {label}
              </text>
            </g>
          )
        })}

        {/* Points riding out to each row's partner, in its colour. */}
        {ROOTED_ROWS.map((row) => {
          const rgb = PARTNER_DOTS[row * 3].rgb!
          return (
            <g
              key={`rider-${row}`}
              className="hp-rider"
              style={{ offsetPath: `path("${rootPath(row)}")`, animationDelay: `${(row * ROOT_CYCLE) / 3}s` }}
            >
              <circle r={4} fill={`rgb(${rgb} / 0.2)`} />
              <circle r={1.9} fill={`rgb(${rgb})`} />
            </g>
          )
        })}

        {/* The root: the BOOTH mark — the exact paths from the brand files
            (public/images/booth-icon-black.svg and booth-icon-gray.svg, which
            share one shape), at their own 449×265 aspect. --pg-mark carries
            each file's colour: #363636 on light, #6F6F6F on dark. */}
        <g>
          <svg
            x={ROOT.x - MARK_W / 2}
            y={ROOT.y - MARK_H / 2}
            width={MARK_W}
            height={MARK_H}
            viewBox="0 0 449 265"
            preserveAspectRatio="xMidYMid meet"
            fill="var(--pg-mark)"
          >
            {BOOTH_MARK_PATHS.map((d) => (
              <path key={d.slice(0, 24)} d={d} />
            ))}
          </svg>
          <circle
            cx={ROOT_OUT}
            cy={ROOT.y}
            r={4}
            className="hp-ring"
            stroke="var(--pg-faint)"
            strokeWidth={0.8}
            style={{ transformOrigin: `${ROOT_OUT}px ${ROOT.y}px` }}
          />
          <circle cx={ROOT_OUT} cy={ROOT.y} r={1.9} fill="var(--pg-fg)" />
        </g>
      </svg>
    </div>
  )
}

function CityDrawing() {
  // Blocks: [x, y, w, h] in a 240×150 field. Irregular on purpose — a real
  // city isn't a checkerboard, and the route reads better threading gaps.
  const blocks: [number, number, number, number][] = [
    [8, 8, 36, 22], [52, 8, 32, 22], [100, 8, 42, 22], [158, 8, 30, 22], [196, 8, 36, 22],
    [8, 38, 18, 28], [52, 48, 32, 18], [100, 48, 42, 18], [158, 48, 30, 36], [196, 38, 36, 46],
    [8, 82, 18, 18], [52, 82, 32, 18], [100, 82, 42, 36], [158, 100, 30, 26],
    [8, 116, 36, 26], [52, 116, 32, 26], [158, 132, 74, 10], [196, 100, 36, 26],
  ]
  return (
    <div className="absolute inset-0 grid place-items-center px-6 py-5">
      {/* Inline max width: the plate has no intrinsic size and an arbitrary
          max-w class can miss the build. Wide enough to fill the article hero;
          on a list card the height cap takes over. */}
      <svg viewBox="0 0 240 150" className="h-full w-full" style={{ maxWidth: 560, overflow: "visible" }} fill="none">
        {/* Blocks — the quietest layer, so the streets between them read as the map. */}
        {blocks.map(([x, y, w, h], i) => (
          <rect
            key={i}
            x={x}
            y={y}
            width={w}
            height={h}
            rx={4}
            fill="var(--pg-card)"
            stroke="var(--pg-card-edge)"
            strokeWidth={1}
            opacity={0.7 + ((i * 5) % 4) * 0.1}
          />
        ))}

        {/* The route: a street drawn dashed, then inked as it's travelled. */}
        <path d={CITY_ROUTE} stroke="var(--pg-faint)" strokeWidth={1} strokeDasharray="3 4" />
        <path
          d={CITY_ROUTE}
          className="hp-ink"
          stroke="rgb(var(--acc) / 0.85)"
          strokeWidth={1.5}
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ strokeDasharray: 400, strokeDashoffset: 400 }}
        />

        {/* Shops along the way. First is where the barber is; last is the next chair. */}
        <g>
          <rect x={26} y={100} width={16} height={16} rx={4} fill="var(--pg-card)" stroke="rgb(var(--acc) / 0.7)" strokeWidth={1} />
          <rect x={30} y={104} width={8} height={8} rx={1.5} fill="rgb(var(--acc) / 0.75)" />
        </g>
        <g>
          <rect x={84} y={32} width={16} height={16} rx={4} fill="var(--pg-card)" stroke="var(--pg-card-edge)" strokeWidth={1} />
          <rect x={88} y={36} width={8} height={8} rx={1.5} fill="var(--pg-muted)" opacity={0.7} />
        </g>
        <g className="hp-arrive">
          <circle cx={206} cy={92} r={14} className="hp-ring" stroke="rgb(var(--acc) / 0.8)" strokeWidth={1} fill="none" />
          <rect x={198} y={84} width={16} height={16} rx={4} fill="rgb(var(--acc))" />
          <rect x={202} y={88} width={8} height={8} rx={1.5} fill="#0B0B0D" opacity={0.85} />
        </g>

        {/* The barber — rides the street. */}
        <g className="hp-rider" style={{ offsetPath: `path("${CITY_ROUTE}")` }}>
          <circle r={5.5} fill="rgb(var(--acc))" />
          <circle r={9} fill="rgb(var(--acc) / 0.25)" />
        </g>
      </svg>
    </div>
  )
}

/* Fig. 06 — channel split. Three bars for views, bookings, revenue — the
   three numbers attribution asks you to hold apart. */
function ChannelsDrawing() {
  const bars = [
    { h: 72, label: "Views" },
    { h: 48, label: "Bookings" },
    { h: 88, label: "Revenue" },
  ]
  return (
    <div className="absolute inset-0 grid place-items-center px-10 py-2.5">
      <div className="flex w-full max-w-[220px] items-end justify-between gap-4">
        {bars.map((b) => (
          <div key={b.label} className="flex flex-1 flex-col items-center gap-2">
            <span
              className="block w-full rounded-[6px]"
              style={{
                height: b.h,
                background: INK,
                boxShadow: "0 12px 28px -12px rgb(var(--acc) / 0.85)",
              }}
            />
            <span className="text-[9px] tracking-tight" style={{ color: "var(--pg-muted)" }}>
              {b.label}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

/* Fig. 07 — the dossier. Stacked financial records for underwriting: rent
   statements, occupancy summaries, and a trend line a lender can read at a glance. */
function DossierDrawing() {
  return (
    <div className="absolute inset-0 grid place-items-center px-9 py-2.5">
      <div className="relative h-[148px] w-[188px]">
        {[2, 1, 0].map((i) => (
          <div
            key={i}
            className="absolute inset-0 rounded-[10px]"
            style={{
              background: i === 0 ? "linear-gradient(165deg, var(--pg-bg) 0%, rgb(var(--acc) / 0.08) 100%)" : "var(--pg-card)",
              border: "1px solid var(--pg-card-edge)",
              transform: `translate(${i * 8}px, ${i * -6}px) rotate(${i * -1.8}deg)`,
              boxShadow: "0 18px 40px -30px rgba(0,0,0,0.95)",
            }}
          />
        ))}
        <div className="absolute inset-0 p-4">
          <div className="flex items-center justify-between gap-2">
            <span className="text-[9px] font-medium tracking-tight" style={{ color: "rgb(var(--acc) / 0.95)" }}>
              Rent summary
            </span>
            <span
              className="rounded-full px-2 py-0.5 text-[8px] font-medium"
              style={{ background: "rgb(var(--acc) / 0.14)", color: "rgb(var(--acc))" }}
            >
              90 days
            </span>
          </div>
          {[0.92, 0.78, 0.64].map((w, i) => (
            <span
              key={i}
              className="mb-2 block h-[3px] rounded-full"
              style={{ width: `${w * 100}%`, background: "var(--pg-line)" }}
            />
          ))}
          <svg viewBox="0 0 150 52" className="mt-2 h-[52px] w-full overflow-visible">
            <polyline
              className="hp-ink"
              points="8,40 34,32 58,28 82,18 106,14 130,8"
              fill="none"
              stroke="rgb(var(--acc))"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            {[8, 34, 58, 82, 106, 130].map((x, i) => (
              <circle key={i} cx={x} cy={[40, 32, 28, 18, 14, 8][i]} r="2.5" fill="rgb(var(--acc))" />
            ))}
            <line x1="4" y1="46" x2="146" y2="46" stroke="var(--pg-line)" strokeWidth="1" />
          </svg>
          <div className="mt-3 flex items-center gap-2">
            <span
              className="grid h-5 w-5 place-items-center rounded-full text-[10px]"
              style={{ background: INK, color: "#0B0B0D" }}
            >
              ✓
            </span>
            <span className="text-[9px]" style={{ color: "var(--pg-muted)" }}>
              Deposits matched · Chairs occupied
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}

/* Fig. 05 — the dial. A points gauge with the multiplier at the middle: the
   payment you were making anyway, turned into a number that moves. */
function DialDrawing() {
  const R = 46
  const C = 2 * Math.PI * R
  // 270° sweep, filled to 68% — enough to read as progress, not as done.
  const sweep = 0.75
  const filled = 0.68
  return (
    <div className="absolute inset-0 grid place-items-center py-2">
      <div className="relative">
        <svg viewBox="0 0 120 120" className="h-[136px] w-[136px] -rotate-[225deg]">
          <defs>
            <linearGradient id="hp-dial" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="rgb(var(--acc))" />
              <stop offset="100%" stopColor="rgb(var(--acc) / 0.45)" />
            </linearGradient>
          </defs>
          <circle
            cx="60"
            cy="60"
            r={R}
            fill="none"
            stroke="var(--pg-line)"
            strokeWidth="7"
            strokeLinecap="round"
            strokeDasharray={`${C * sweep} ${C}`}
          />
          <circle
            className="hp-sweep"
            cx="60"
            cy="60"
            r={R}
            fill="none"
            stroke="url(#hp-dial)"
            strokeWidth="7"
            strokeLinecap="round"
            strokeDasharray={`${C * sweep * filled} ${C}`}
          />
        </svg>
        {[0, 1, 2, 3, 4].map((i) => (
          <span
            key={i}
            className="absolute left-1/2 top-1/2 h-[62px] w-px origin-top"
            style={{
              background: "var(--pg-line)",
              transform: `translate(-50%,0) rotate(${-135 + i * 67.5}deg)`,
              opacity: 0.7,
            }}
          />
        ))}
        <div className="absolute inset-0 grid place-content-center text-center">
          <span
            className="text-[26px] font-semibold leading-none tracking-[-0.04em]"
            style={{ color: "rgb(var(--acc))" }}
          >
            2×
          </span>
          <span className="mt-1.5 text-[10px]" style={{ color: "var(--pg-muted)" }}>
            on autopay
          </span>
        </div>
      </div>
    </div>
  )
}
